import type {
    AgencyDashboardPeriod,
    AgencyTripRow,
    TripState,
} from '@/integrations/supabase/rpc-types';
import type { TranslationKey } from '@/types/i18n.types';
import {
    DATE_FORMAT,
    MONEY_FORMAT,
    parseDateOnly,
} from '@/lib/dashboard/clients/clients-list.export';
import {
    TRIP_STATE_LABEL_KEYS,
    itineraryStatusLabel,
    tripAdvisorName,
} from '@/lib/dashboard/trips/trip-display';

/**
 * Export XLSX della lista viaggi: una riga per itinerario.
 *
 * Come la lista clienti non calcola nulla: ogni importo arriva da `get_agency_trips`. Le
 * righe sono quelle che la tab ha in mano -- la vista filtrata oppure l'insieme intero --
 * e il secondo foglio dichiara quale dei due e' finito nel file.
 */

export type TripsListT = (key: TranslationKey, options?: Record<string, unknown>) => string;

export interface BuildTripsListOptions {
    t: TripsListT;
    /** 'all' = tutti i viaggi senza filtri, 'filtered' = la vista corrente. */
    scope: 'all' | 'filtered';
    /** Periodo con cui la RPC ha prodotto le righe; `null` = nessun limite. */
    period?: AgencyDashboardPeriod | null;
    stateFilter?: TripState | 'all';
    search?: string;
    advisorName?: string | null;
    locale?: string;
}

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } } as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ws = any;

interface SheetColumn {
    header: string;
    width: number;
    numFmt?: string;
}

export async function buildTripsListWorkbook(
    rows: AgencyTripRow[],
    { t, scope, period = null, stateFilter = 'all', search, advisorName, locale = 'it-IT' }: BuildTripsListOptions
): Promise<Blob> {
    const module = await import('exceljs');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ExcelJS = ((module as any).default ?? module) as any;

    const workbook = new ExcelJS.Workbook();
    const generatedAt = new Date();
    workbook.created = generatedAt;
    workbook.views = [{ activeTab: 0, firstSheet: 0, visibility: 'visible' }];

    // ------------------------------------------------------------------ Viaggi (foglio 1)
    const sheet = workbook.addWorksheet(t('dashboard.overview.trips.export.sheetTrips'));
    const k = (key: string) => t(`dashboard.overview.trips.export.${key}` as TranslationKey);

    const columns: SheetColumn[] = [
        { header: k('columnTrip'), width: 32 },
        { header: k('columnState'), width: 15 },
        { header: k('columnItineraryStatus'), width: 18 },
        { header: k('columnFirstName'), width: 16 },
        { header: k('columnLastName'), width: 16 },
        { header: k('columnEmail'), width: 28 },
        { header: k('columnPhone'), width: 16 },
        { header: k('columnAdvisor'), width: 22 },
        { header: k('columnDestinations'), width: 28 },
        { header: k('columnStartDate'), width: 13, numFmt: DATE_FORMAT },
        { header: k('columnEndDate'), width: 13, numFmt: DATE_FORMAT },
        { header: k('columnItineraryTotal'), width: 16, numFmt: MONEY_FORMAT },
        { header: k('columnPlanTotal'), width: 16, numFmt: MONEY_FORMAT },
        { header: k('columnPaid'), width: 16, numFmt: MONEY_FORMAT },
        { header: k('columnRemaining'), width: 16, numFmt: MONEY_FORMAT },
        { header: k('columnOverdue'), width: 16, numFmt: MONEY_FORMAT },
        { header: k('columnInstallments'), width: 12 },
        { header: k('columnCreatedAt'), width: 13, numFmt: DATE_FORMAT },
    ];

    sheet.columns = columns.map((column) => ({ width: column.width }));

    const header = sheet.addRow(columns.map((column) => column.header));
    header.font = { bold: true };
    header.eachCell((cell: Ws) => {
        cell.fill = HEADER_FILL;
    });

    rows.forEach((trip) => {
        const row = sheet.addRow([
            trip.title,
            t(TRIP_STATE_LABEL_KEYS[trip.trip_state]),
            itineraryStatusLabel(trip.status, t),
            trip.client_first_name ?? '',
            trip.client_last_name ?? '',
            trip.client_email ?? '',
            trip.client_phone ?? '',
            tripAdvisorName(trip) ?? '',
            trip.destinations.join(', '),
            parseDateOnly(trip.start_date) ?? '',
            parseDateOnly(trip.end_date) ?? '',
            trip.total ?? '',
            // Senza piano la terna non esiste: celle vuote, non zeri che si leggerebbero
            // come un mancato pagamento.
            trip.has_plan ? trip.plan_total : '',
            trip.has_plan ? trip.paid_amount : '',
            trip.has_plan ? trip.remaining_amount : '',
            trip.has_plan ? trip.overdue_amount : '',
            trip.has_plan ? `${trip.installments_paid_count}/${trip.installments_count}` : '',
            parseDateOnly(trip.created_at) ?? '',
        ]);

        columns.forEach((column, index) => {
            if (column.numFmt) row.getCell(index + 1).numFmt = column.numFmt;
        });
    });

    sheet.views = [{ state: 'frozen', ySplit: header.number }];
    sheet.autoFilter = {
        from: { row: header.number, column: 1 },
        to: { row: header.number, column: columns.length },
    };

    // ------------------------------------------------------------------ Contesto (foglio 2)
    const context = workbook.addWorksheet(t('dashboard.overview.trips.export.sheetContext'));
    context.columns = [{ width: 30 }, { width: 60 }];

    const contextHeader = context.addRow([k('fieldColumn'), k('valueColumn')]);
    contextHeader.font = { bold: true };
    contextHeader.eachCell((cell: Ws) => {
        cell.fill = HEADER_FILL;
    });

    const noFilter = k('noFilter');
    context.addRow([k('generatedOn'), generatedAt.toLocaleString(locale)]);
    context.addRow([k('scope'), scope === 'all' ? k('scopeAll') : k('scopeFiltered')]);
    if (scope === 'filtered') {
        // Le chiavi a schermo sono period7d / period30d / period90d / periodYtd.
        context.addRow([
            k('period'),
            period
                ? t(`dashboard.overview.period${period === 'ytd' ? 'Ytd' : period}` as TranslationKey)
                : k('periodAll'),
        ]);
        context.addRow([
            k('stateFilter'),
            stateFilter === 'all' ? noFilter : t(TRIP_STATE_LABEL_KEYS[stateFilter]),
        ]);
        context.addRow([k('search'), search?.trim() || noFilter]);
        context.addRow([k('advisor'), advisorName || k('allAdvisors')]);
    }
    context.addRow([k('count'), rows.length]);

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
}

/** Nome file: `viaggi_2026-09-11.xlsx` / `viaggi_filtrati_2026-09-11.xlsx`. */
export function tripsListFilename(prefix: string): string {
    const day = new Date().toISOString().slice(0, 10);
    return `${prefix}_${day}.xlsx`;
}
